import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";

import { UNSAFE_LocationContext } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useActionLoggerOptional } from "@/services/logger/logger-hooks";
import {
  getScreenMetadataFromPath,
  loggerContextManager,
} from "@/services/logger/smart-context-provider";
import { normalizeTitle } from "./alert-dialog";
import {
  LogCategories,
  LogEventTypeEnum,
  LogSubCategories,
  LogTags,
} from "@/services/logger/logger-types";
import { capitalizeInitialLetter } from "@/lib/string-util";
import { useCallback, useEffect, useRef } from "react";
import { getDialogOpenedDurationMs } from "@/services/logger/logger-utils";

/** Set by DialogClose when X/close button is clicked; Dialog skips its "closed" log in that case to avoid duplicate logs. */
let closeButtonClickedForLog = false;

function getNodeText(node: React.ReactNode): string {
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(getNodeText).join(" ").trim();
  if (React.isValidElement(node)) return getNodeText(node.props.children);
  return "";
}

type DialogRootProps = React.ComponentPropsWithoutRef<
  typeof DialogPrimitive.Root
> & {
  /** Category override for dialog logs. Falls back to the active route's category/subCategory when omitted. */
  logTag?: LogTags;
  logCategory?: LogCategories;
  logSubCategory?: LogSubCategories;
  /** Optional friendly name for logging, e.g. "Change Password" */
  logDialogName?: string;
  /** What triggered the dialog, e.g. "Header menu" – included in logs and dialog context for buttons inside */
  logTrigger?: string;
  logType?: LogEventTypeEnum;
  /** Extra metadata merged into the "opened"/"closed" log events (e.g. claim/member ids) */
  logMetadata?: Record<string, unknown>;
};

const Dialog = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Root>,
  DialogRootProps
>(
  (
    {
      logDialogName,
      logTrigger,
      logTag = LogTags.MODULE_ACCESS,
      logCategory: logCategoryProp,
      logSubCategory: logSubCategoryProp,
      logType = LogEventTypeEnum.PAGE_VIEW,
      logMetadata = {},
      onOpenChange,
      open,
      defaultOpen,
      ...props
    },
    _ref,
  ) => {
    const { logAction } = useActionLoggerOptional();
    const locationContext = React.useContext(UNSAFE_LocationContext);
    const location = locationContext?.location;
    const routeMeta = location
      ? getScreenMetadataFromPath(location.pathname)
      : null;
    const logCategory =
      logCategoryProp ?? routeMeta?.category ?? LogCategories.NONE;
    const logSubCategory =
      logSubCategoryProp ?? routeMeta?.subCategory ?? LogSubCategories.NONE;
    const logActionRef = useRef(logAction);
    logActionRef.current = logAction;
    // Held in a ref so log calls read the latest metadata without adding it to effect
    // dependency arrays (a new object each render would otherwise re-fire open/close logs).
    const logMetadataRef = useRef(logMetadata);
    logMetadataRef.current = logMetadata;
    const lastOpenRef = useRef<boolean | undefined>(undefined);
    /**
     * Set to true when handleOpenChange has already emitted the close log (backdrop/ESC/X-button
     * path) so the controlled-mode useEffect cleanup does not double-log.
     */
    const closeAlreadyHandledRef = useRef(false);
    /**
     * For uncontrolled + defaultOpen=true: set to true when handleOpenChange pops the dialog
     * context on close so the mount-effect cleanup does not pop it a second time.
     */
    const uncontrolledContextPoppedRef = useRef(false);

    // Use a ref so cleanup functions always call the freshest version of closeWithLog.
    const closeWithLog = useCallback(() => {
      if (!logDialogName) return;
      logActionRef.current(
        normalizeTitle(`${logDialogName} Dialog Closed`),
        { ...logMetadataRef.current },
        {
          debounceMs: 100,
          suppressDuplicate: true,
          category: logCategory,
          useTitleAsIs: true,
          type: logType,
          duration: getDialogOpenedDurationMs(),
        },
      );
    }, [logDialogName, logCategory, logType]);

    const closeWithLogRef = useRef(closeWithLog);
    closeWithLogRef.current = closeWithLog;

    // Controlled mode (open prop defined):
    // – Log "dialog opened" when open becomes true.
    // – Log "dialog closed" in cleanup when open transitions true → false, UNLESS
    //   handleOpenChange already fired the close log (backdrop / ESC / X-button paths).
    useEffect(() => {
      if (!logDialogName || open === undefined) return;
      if (open) {
        lastOpenRef.current = true;
        closeAlreadyHandledRef.current = false;
        loggerContextManager.pushDialogContext({
          dialogName: logDialogName,
          openedFrom: logTrigger,
          tag: logTag ?? LogTags.NONE,
          category: logCategory ?? LogCategories.NONE,
          subCategory: logSubCategory ?? LogSubCategories.NONE,
          type: logType,
          openedAtMs: Date.now(),
        });
        logActionRef.current(
          normalizeTitle(`${logDialogName} dialog opened`),
          { ...logMetadataRef.current },
          {
            debounceMs: 100,
            suppressDuplicate: true,
            useTitleAsIs: true,
            type: logType,
          },
        );
        return () => {
          // Programmatic close (setDialogOpen(false)) never calls onOpenChange, so
          // closeAlreadyHandledRef stays false and we emit the close log here.
          if (!closeAlreadyHandledRef.current) {
            closeWithLogRef.current();
          }
          closeAlreadyHandledRef.current = false;
          loggerContextManager.popDialogContext();
        };
      }
    }, [
      open,
      logDialogName,
      logTrigger,
      logCategory,
      logType,
      logSubCategory,
      logTag,
    ]);

    // Uncontrolled mode with defaultOpen=true:
    // Radix never calls onOpenChange(true) for defaultOpen, so we push context and log
    // "dialog opened" once on mount. The cleanup pops context only if handleOpenChange
    // hasn't already done so when the dialog was closed by the user.
    useEffect(() => {
      if (!logDialogName || open !== undefined || !defaultOpen) return;
      uncontrolledContextPoppedRef.current = false;
      loggerContextManager.pushDialogContext({
        dialogName: logDialogName,
        openedFrom: logTrigger,
        tag: logTag ?? LogTags.NONE,
        category: logCategory ?? LogCategories.NONE,
        subCategory: logSubCategory ?? LogSubCategories.NONE,
        type: logType,
        openedAtMs: Date.now(),
      });
      logActionRef.current(
        normalizeTitle(`${logDialogName} dialog opened`),
        {},
        {
          debounceMs: 100,
          suppressDuplicate: true,
          useTitleAsIs: true,
          type: logType,
        },
      );
      return () => {
        if (!uncontrolledContextPoppedRef.current) {
          loggerContextManager.popDialogContext();
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleOpenChange = (nextOpen: boolean) => {
      // Uncontrolled mode: push context when dialog opens via trigger.
      // For defaultOpen=true the open is handled by the mount effect; this block
      // only fires for open here (never fires for defaultOpen initial state).
      if (logDialogName && open === undefined && nextOpen) {
        loggerContextManager.pushDialogContext({
          dialogName: logDialogName,
          openedFrom: logTrigger,
          openedAtMs: Date.now(),
        });
      }

      if (logDialogName && lastOpenRef.current !== nextOpen) {
        lastOpenRef.current = nextOpen;

        if (!nextOpen && closeButtonClickedForLog) {
          // X-button path: DialogClose logged the button interaction; also emit the dialog-close
          // log here and mark as handled so the controlled useEffect cleanup does not double-log.
          closeButtonClickedForLog = false;
          closeWithLog();
          closeAlreadyHandledRef.current = true;
          if (open === undefined) {
            uncontrolledContextPoppedRef.current = true;
            loggerContextManager.popDialogContext();
          }
        } else if (!nextOpen) {
          // closeWithLog() reads context for duration — pop AFTER it, not before.
          // This block also runs before onOpenChange?.(nextOpen) below so the close log
          // is guaranteed to fire before any onOpenChange side-effect (e.g. dispatching
          // dequeueAppAlertDialog) that could unmount this component synchronously.
          closeWithLog();
          closeAlreadyHandledRef.current = true;
          if (open === undefined) {
            uncontrolledContextPoppedRef.current = true;
            loggerContextManager.popDialogContext();
          }
        } else {
          // Open via user interaction (uncontrolled, non-defaultOpen trigger).
          logActionRef.current(
            normalizeTitle(`${logDialogName} Dialog Opened`),
            {
              ...logMetadataRef.current,
              dialogName: logDialogName,
              openedFrom: logTrigger,
              open: nextOpen,
            },
            {
              debounceMs: 100,
              suppressDuplicate: true,
              category: logCategory,
              useTitleAsIs: true,
              type: logType,
            },
          );
        }
      }

      // Call onOpenChange LAST so all logging and context updates complete before any
      // side-effect in the callback (e.g. dispatching Redux actions that unmount this
      // component synchronously via useSyncExternalStore).
      onOpenChange?.(nextOpen);
    };

    return (
      <DialogPrimitive.Root
        open={open}
        defaultOpen={defaultOpen}
        onOpenChange={handleOpenChange}
        {...props}
      />
    );
  },
);

Dialog.displayName = DialogPrimitive.Root.displayName;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

type DialogCloseProps = React.ComponentPropsWithoutRef<
  typeof DialogPrimitive.Close
> & {
  /** Optional name to associate this close with a dialog */
  logDialogName?: string;
  /** Optional explicit action label (defaults from children text or "Close") */
  logActionLabel?: string;
  logCategory?: LogCategories;
};

const DialogClose = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Close>,
  DialogCloseProps
>(
  (
    {
      logDialogName,
      logActionLabel: _logActionLabel,
      logCategory,
      onClick,
      children,
      asChild,
      ...props
    },
    ref,
  ) => {
    const { logAction } = useActionLoggerOptional();

    const handleClick: React.MouseEventHandler<HTMLButtonElement> = (e) => {
      if (logDialogName) {
        closeButtonClickedForLog = true;
        // When asChild (e.g. with Button), the child already logs; skip to avoid duplicate
        if (!asChild) {
          const label = (getNodeText(children) || "Close").trim().toLowerCase();
          const title = capitalizeInitialLetter(
            `${logDialogName} ${label} button`.split("_").join(" "),
          );
          logAction(
            title,
            {},
            {
              debounceMs: 300,
              suppressDuplicate: true,
              category: logCategory,
              useTitleAsIs: true,
            },
          );
        }
      }
      onClick?.(e);
    };

    return (
      <DialogPrimitive.Close
        ref={ref}
        onClick={handleClick}
        asChild={asChild}
        {...props}
      >
        {children}
      </DialogPrimitive.Close>
    );
  },
);
DialogClose.displayName = DialogPrimitive.Close.displayName;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/75 backdrop-blur-[2px] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

type DialogContentProps = React.ComponentPropsWithoutRef<
  typeof DialogPrimitive.Content
> & {
  /** Optional; when set, the built-in X close button will log with this name */
  logDialogName?: string;
  logCategory?: LogCategories;
  /** Hide the built-in top-right X close button (e.g. when a custom header renders its own) */
  hideCloseButton?: boolean;
};

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(
  (
    {
      className,
      children,
      logDialogName,
      logCategory,
      hideCloseButton,
      ...props
    },
    ref,
  ) => (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        style={{ borderRadius: "12px" }}
        className={cn(
          "fixed left-1/2 top-1/2 z-50 grid w-full max-w-lg -translate-x-1/2 -translate-y-1/2 gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
          className,
        )}
        {...props}
      >
        {children}
        {!hideCloseButton && (
          <DialogClose
            logDialogName={logDialogName}
            logActionLabel="Close"
            logCategory={logCategory}
            className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground"
          >
            {/* 4.1.2 Name, Role, Value — A: an unlabeled <svg> sibling to the sr-only
                text is exposed as its own unnamed accessibility-tree node by some
                browser/AT pairings (Safari/VoiceOver appends "and 1 more item" to the
                button's announcement for it) — aria-hidden removes it from that tree
                entirely, leaving only the sr-only text to name the button. */}
            <X aria-hidden="true" className="size-6" />
            <span className="sr-only">Close</span>
          </DialogClose>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  ),
);
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      className,
    )}
    {...props}
  />
);
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className,
    )}
    {...props}
  />
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight",
      className,
    )}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
